import {Date as ApiDate, FullDate as ApiFullDate} from 'gabi-api-ts/v2/common/date/date';
import {SignalType} from 'gabi-api-ts/v2/common/signals/signals';
import {SignalQueryServiceClient} from 'gabi-api-ts/v2/signal/query/signal_query.client';
import Styled from 'styled-components';

import GuidedTourHealthReport from '@/components/business/analytics/guidedTour/guided-tour-health-report';
import {HealthReportDuration} from '@/components/business/analytics/health-report/health-report-duration';
import {HealthReportNoRecording} from '@/components/business/analytics/health-report/health-report-no-recording';
import {HealthReportSpotSignal} from '@/components/business/analytics/health-report/health-report-spot-signal';
import {BiometricHistorySpotData,HealthReportSpotSignalsTable} from '@/components/business/analytics/health-report/health-report-spot-signals-table';
import {HealthReportWrapper} from '@/components/business/analytics/health-report/health-report-wrapper';
import ColoredContainer from '@/components/layout/colored-container';
import LoadingView from '@/components/static/loading-view';
import {HealthReportDashboardTypeEnum} from '@/enum/health-report-dashboard-type-enum';
import {usePatientData} from '@/hooks/backend/use-patient-data';
import { useBackendQuery } from '@/hooks/use-backend-query';
import { SignalDefinitions } from '@/model/signal-definitions/signal-definition';
import timelineIcon from '@/public/icons/timeline-icon.svg';
import {PatientContextService} from '@/services/patient-context-service';
import {useTutorialStatus} from '@/services/tutorial-service';
import {colors, medias} from '@/themes/darkmode';
import {addDays, apiDateToday, getSecondsDifference, isAfter, subDays} from '@/util/apidate-util';

type HealthReportLastAvailableNightSpotProps = {
    id_patient: string;
    className: string,
};

function HealthReportLastAvailableNightSpotUnstyled(props: HealthReportLastAvailableNightSpotProps) {
    const patientId = props.id_patient ?? '';
    let dashboardHasContent = false;
    const tutorialStatus = useTutorialStatus();

    const [patientDataLoading, patientData] = usePatientData({
        patientId: patientId,
        memoize: true,
    });

    /*const [signalsEventConfigurationLoading, eventConfiguration] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getEventConfiguration,
        memoize: true,
        data: { patientId: { id: patientId } },
        disabled: patientDataLoading || (patientData?.patientType === PatientType.Adult),
        grpcOptions: {
            errorHandler: ignoreVP39(defaultEventConfiguration),
        },
    });*/

    let dateFrom: ApiDate = { year: 2020, month: 1, day: 1 };
    let dateTo: ApiDate = { year: 2020, month: 1, day: 1 };
    if (patientData?.lastRecordingDataTime?.date) {
        dateFrom = patientData.lastRecordingDataTime.date;
        dateTo = addDays(patientData.lastRecordingDataTime.date, 1);
        const lastRecordingDateLimit = subDays(apiDateToday(), 30);
        dashboardHasContent = isAfter(dateFrom, lastRecordingDateLimit);
    }

    const [ biometricHistoryLoading, biometricHistory, /*eventsListError*/ ] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getBiometricHistory,
        memoize: false,
        data: {
            patientId: {id: patientId},
            from: dateFrom,
            to: dateTo,
            signalType: [
                SignalType.Signal_PR,
                SignalType.Signal_SPO2,
                SignalType.Signal_BP_DIA,
                SignalType.Signal_BP_SYS,
                SignalType.NEWS2,
            ],
        },
        // grpcOptions: {
        //     errorHandler: ignoreVP39({
        //         event: [],
        //     } as TypedEventsList),
        // },
        transformer: (response => {
            const signals = response.signals;
            const biometricChartData: BiometricHistorySpotData[] = response.entries.map(point => {
                const fullDate = point.timestamps;
                const values = point.values.map((val,i) => {
                    return {
                        signalType: signals[i],
                        value: val,
                    };
                });
                const seconds = getSecondsDifference({
                    date: dateFrom,
                    time: {hour: 0, minutes: 0, seconds: 0, milliSeconds: 0, offset: 0},
                }, fullDate!);
                return {
                    fullDate: fullDate as ApiFullDate,
                    values: values,
                    seconds: seconds,
                };
            });
            return {
                ...response,
                transformed: biometricChartData,
            };
        })
    }, []);

    const loading = (
        (patientDataLoading || !patientData) ||
        (biometricHistoryLoading || !biometricHistory)
    );

    if (loading) {
        return (
            <HealthReportWrapper
                patientId={patientId}
                patientData={patientData}
                activeDashboard={HealthReportDashboardTypeEnum.LAST_24H}
            >
                <LoadingView />
            </HealthReportWrapper>
        );
    }
    //--------------------------------------------------------------------------------------------------------------

    if (!dashboardHasContent) {
        return (
            <HealthReportWrapper
                patientId={patientId}
                patientData={patientData}
                activeDashboard={HealthReportDashboardTypeEnum.LAST_24H}
            >
                <HealthReportNoRecording patient={patientData} activeDashboard={HealthReportDashboardTypeEnum.LAST_24H} />
            </HealthReportWrapper>
        );
    }
    //--------------------------------------------------------------------------------------------------------------

    const patientContext = PatientContextService.getPatientContext(patientData!);

    const hasEvents = patientContext.supportsEvents;
    const hasEventsList = false;

    return (
        <div className={`${props.className} ${hasEventsList ? 'has-events-list' : ''} ${hasEvents ? 'has-events' : ''}`}>
            <HealthReportWrapper
                patientId={patientId}
                patientData={patientData}
                activeDashboard={HealthReportDashboardTypeEnum.LAST_24H}
            >
                {tutorialStatus.guidedTourHealthReport && (
                    <GuidedTourHealthReport />
                )}

                {dashboardHasContent && (
                    <div className="health-report-switch-wrapper">
                        <HealthReportDuration patientId={patientId} dateFrom={dateFrom} dateTo={dateTo}/>
                        {/*<Link*/}
                        {/*    to={`/patients/${patientId}/timeline/${timelineDate}`}*/}
                        {/*    id="button-timeline"*/}
                        {/*    className="button-link"*/}
                        {/*>*/}
                        {/*    {t('healthReportDashboardNavigation.dailyBiometrics') ?? 'Daily biometrics'}*/}
                        {/*</Link>*/}
                    </div>
                )}

                {(dashboardHasContent) && (
                    <div className={'health-report-content health-report-content-view'}>
                        <SubChart
                            patientId={patientId}
                            signalType={SignalType.Signal_PR}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                        />

                        <SubChart
                            patientId={patientId}
                            signalType={SignalType.Signal_SPO2}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                        />

                        <SubChart
                            patientId={patientId}
                            signalType={SignalType.Signal_BP_SYS}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                        />

                        <SubChart
                            patientId={patientId}
                            signalType={SignalType.Signal_BP_DIA}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                        />

                        <SubChart
                            patientId={patientId}
                            signalType={SignalType.NEWS2}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                        />

                        <HealthReportSpotSignalsTable
                            data={biometricHistory?.transformed}
                        />
                    </div>
                )}
            </HealthReportWrapper>
        </div>
    );
}

type SubChartProps = {
    patientId: string;
    signalType: SignalType;
    dateFrom: ApiDate;
    dateTo: ApiDate;
};

function SubChart({dateFrom, dateTo, patientId, signalType}: SubChartProps) {
    return (
        <ColoredContainer className="widget-aggregated-signal" color={SignalDefinitions[signalType].colors.normal}>
            <HealthReportSpotSignal
                patientId={patientId}
                signalType={signalType}
                dateFrom={dateFrom}
                dateTo={dateTo}
            />
        </ColoredContainer>
    );
}

//language=SCSS
const HealthReportLastAvailableNightSpot = Styled(HealthReportLastAvailableNightSpotUnstyled)`
& {
    .health-report-switch-wrapper {
        position:relative;
        margin-bottom:20px;
        #button-timeline {
            background: ${colors.buttonCreate.background} url(${timelineIcon}) no-repeat 15px center;
            background-size: 25px;
            position: absolute;
            right: 0;
            top: 0;
            padding: 10px 15px 10px 50px;
            border-radius: 5px;
            &:hover, &:focus {
                color: #fff;
                background-color: ${colors.buttonCreate.backgroundHover};
            }
        }
    }
    
    .health-report-content {
        display: grid;
        grid-template-columns: minmax(0, 2fr);
        &.has-events {
            grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
        }
        @media screen and (${medias.xlMin}) {
            &.has-events.has-events-list {
                grid-template-columns: minmax(0, 2fr) minmax(250px, 1fr) minmax(260px, 1fr);
            }
        }
        
        grid-auto-rows: 340px;
        flex-wrap: nowrap;
        gap: 10px;
    }

    .health-report-events-list {
        display: none;
        @media screen and (${medias.xlMin}) {
            display: block;
        }
        grid-column: 3;
        grid-row: 1;
        grid-row-end: span 2;
    }
    
    .widget-aggregated-signal {
        grid-column: 1;
    }
    
    .widget-events {
        grid-column: 2;
    }
}
`;

export {HealthReportLastAvailableNightSpot};
